/**
 * <s-drag-tab data-key> — a side tab fixed to the screen edge that visitors can drag
 * up or down. A short tap still opens its pop-up; a drag moves the tab and swallows
 * the click. The position is remembered per tab in localStorage ("th-tab-<key>").
 */
class SDragTab extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const storeKey = `th-tab-${this.dataset.key || 'side'}`;

    const clamp = (top) => {
      const margin = 80;
      const max = window.innerHeight - this.offsetHeight - margin * 1.6;
      return Math.min(Math.max(top, margin), Math.max(margin, max));
    };
    const place = (top) => {
      this.style.top = `${clamp(top)}px`;
      this.style.transform = 'none';
      this.style.translate = 'none';
    };

    try {
      const saved = Number(localStorage.getItem(storeKey));
      if (saved) place(saved);
    } catch (e) { /* storage blocked */ }

    let startY = 0;
    let startTop = 0;
    let dragging = false;
    let pointerId = null;
    let suppress = false;

    this.addEventListener('click', (ev) => {
      if (!suppress) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
    }, { capture: true, signal: this.#ac.signal });

    this.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      pointerId = e.pointerId;
      startY = e.clientY;
      startTop = this.getBoundingClientRect().top;
      dragging = false;
    }, opts);

    this.addEventListener('pointermove', (e) => {
      if (e.pointerId !== pointerId) return;
      const dy = e.clientY - startY;
      if (!dragging && Math.abs(dy) > 6) {
        dragging = true;
        this.setPointerCapture(pointerId);
        this.dataset.dragging = '';
      }
      if (dragging) place(startTop + dy);
    }, opts);

    const end = (e) => {
      if (e.pointerId !== pointerId) return;
      pointerId = null;
      if (!dragging) return;
      delete this.dataset.dragging;
      try { localStorage.setItem(storeKey, String(Math.round(this.getBoundingClientRect().top))); } catch (err) { /* storage blocked */ }
      // Swallow the click that follows a drag so the pop-up doesn't open.
      suppress = true;
      setTimeout(() => { suppress = false; }, 350);
      setTimeout(() => { dragging = false; }, 0);
    };
    this.addEventListener('pointerup', end, opts);
    this.addEventListener('pointercancel', end, opts);

    window.addEventListener('resize', () => {
      if (this.style.top) place(parseFloat(this.style.top));
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-drag-tab')) customElements.define('s-drag-tab', SDragTab);
